import {
  CREDENTIAL_MASK,
  type CredentialDto,
  createCredentialInput,
  updateCredentialInput,
} from '@fos/shared/contracts/vault';
import { VAULT_ROTATION_REASON_LABELS } from '@fos/shared/labels';
import { Archive, KeyRound, RefreshCw, Wand2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../../lib/api';
import { trpc } from '../../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../../lib/useFocusInvalid';
import { FormMessage } from '../../../../ui/Blocks';
import { Button } from '../../../../ui/Button';
import { Dialog } from '../../../../ui/Dialog';
import { Field } from '../../../../ui/Field';
import { Input, PasswordInput, Textarea } from '../../../../ui/Input';
import { toast } from '../../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../../mobile';
import { issuesToErrors, zodIssues } from '../../form-utils';
import { dateText, generatePassword } from './vault-utils';

const KEYS = ['label', 'panelUrl', 'login', 'secret', 'externalRef', 'note'] as const;
type Key = (typeof KEYS)[number];
type SecretMode = 'keep' | 'change' | 'remove';

const orNull = (v: string) => (v.trim() ? v.trim() : null);

/**
 * Dodaj / edytuj dostęp (DESIGN §5 „Sejf dostępów”): etykieta, adres panelu, login (jawne), hasło (pole typu password,
 * zapis zaszyfrowany na serwerze; generator), albo link do menedżera haseł, notatka. W edycji hasło zostaje bez zmian,
 * dopóki osoba nie wybierze „Zmień hasło” albo „Usuń z sejfu” (to wymaga potwierdzenia hasłem — modal step-up).
 * Otwarte przypomnienie o zmianie hasła: informacja + „Pomiń przypomnienie”. Archiwizacja w edycji.
 */
export function CredentialFormDialog({
  open,
  onOpenChange,
  assetId,
  credential,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  /** brak = nowy dostęp */
  credential?: CredentialDto;
}) {
  const utils = trpc.useUtils();
  const create = trpc.vault.create.useMutation();
  const update = trpc.vault.update.useMutation();
  const archive = trpc.vault.archive.useMutation();
  const dismiss = trpc.vault.resolveRotationReminder.useMutation();
  const [label, setLabel] = useState('');
  const [panelUrl, setPanelUrl] = useState('');
  const [login, setLogin] = useState('');
  const [secret, setSecret] = useState('');
  const [secretMode, setSecretMode] = useState<SecretMode>('keep');
  const [externalRef, setExternalRef] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu (i czyszczenie sekretu przy zamknięciu)
  useEffect(() => {
    setSecret('');
    if (!open) return;
    setLabel(credential?.label ?? '');
    setPanelUrl(credential?.panelUrl ?? '');
    setLogin(credential?.login ?? '');
    setSecretMode(credential?.hasSecret ? 'keep' : 'change');
    setExternalRef(credential?.externalRef ?? '');
    setNote(credential?.note ?? '');
    setErrors({});
    setFormError(null);
  }, [open]);

  const fail = (err: unknown) => {
    if (appErrorCode(err) === 'VALIDATION_ERROR') {
      const fieldErrors = issuesToErrors(validationIssues(err), KEYS);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
    }
    if (appErrorCode(err) === 'STEP_UP_REQUIRED') {
      setFormError('Zmiana hasła wymaga potwierdzenia. Spróbuj ponownie.');
      return;
    }
    setFormError(
      appErrorCode(err) === 'CONFLICT'
        ? 'Ktoś zmienił ten dostęp. Zamknij okno i spróbuj ponownie.'
        : errorMessage(err),
    );
  };

  const done = async (message: string) => {
    setSecret('');
    toast.success(message);
    await utils.vault.invalidate();
    onOpenChange(false);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setErrors({});
    const fields = {
      label: label.trim(),
      panelUrl: orNull(panelUrl),
      login: orNull(login),
      externalRef: orNull(externalRef),
      note: orNull(note),
    };
    try {
      if (credential) {
        const input: Record<string, unknown> = { ...fields, id: credential.id, version: credential.version };
        if (secretMode === 'change' && secret) input.secret = secret;
        if (secretMode === 'remove') input.secret = null;
        const parsed = updateCredentialInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), KEYS));
        await update.mutateAsync(parsed.data);
        await done('Zapisano dostęp');
      } else {
        const input = { ...fields, assetId, ...(secret ? { secret } : {}) };
        const parsed = createCredentialInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), KEYS));
        await create.mutateAsync(parsed.data);
        await done('Dodano dostęp');
      }
    } catch (err) {
      fail(err);
    }
  };

  const onArchive = async () => {
    if (!credential) return;
    setFormError(null);
    try {
      await archive.mutateAsync({ id: credential.id });
      await done('Dostęp przeniesiony do archiwum');
    } catch (err) {
      fail(err);
    }
  };

  const onDismissReminder = async () => {
    if (!credential?.rotationDue) return;
    setFormError(null);
    try {
      await dismiss.mutateAsync({ id: credential.rotationDue.id, resolution: 'dismissed' });
      toast.success('Pominięto przypomnienie');
      await utils.vault.invalidate();
    } catch (err) {
      fail(err);
    }
  };

  const pending = create.isPending || update.isPending || archive.isPending;
  const showSecretInput = !credential || secretMode === 'change';
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="m"
      title={credential ? 'Edytuj dostęp' : 'Dodaj dostęp'}
      className={FULLSCREEN_ON_PHONE}
      description="Hasło zapisujemy zaszyfrowane. Login i adres panelu są widoczne dla osób z dostępem do sejfu."
      footer={
        <>
          {credential && (
            <Button variant="ghost" icon={Archive} onClick={onArchive} disabled={pending} className="mr-auto">
              Archiwizuj
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="vault-credential-form" loading={pending}>
            {credential ? 'Zapisz' : 'Dodaj dostęp'}
          </Button>
        </>
      }
    >
      <form
        id="vault-credential-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
      >
        {credential?.rotationDue && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg bg-warning-subtle px-3 py-2.5">
            <RefreshCw size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-warning" />
            <p className="min-w-0 flex-1 text-caption text-fg">
              {VAULT_ROTATION_REASON_LABELS[credential.rotationDue.reason]}. Zmień hasło do{' '}
              {dateText(credential.rotationDue.dueOn)}.
            </p>
            <Button variant="ghost" size="s" onClick={onDismissReminder} loading={dismiss.isPending}>
              Pomiń przypomnienie
            </Button>
          </div>
        )}
        <Field label="Nazwa" error={errors.label}>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="np. Panel hostingu, FTP, Baza danych"
            autoComplete="off"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Login" error={errors.login}>
            <Input
              mono
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </Field>
          <Field label="Adres panelu" error={errors.panelUrl}>
            <Input
              type="url"
              inputMode="url"
              value={panelUrl}
              onChange={(e) => setPanelUrl(e.target.value)}
              placeholder="https://"
              autoComplete="off"
            />
          </Field>
        </div>

        {showSecretInput ? (
          <Field
            label="Hasło"
            hint={credential ? 'Nowe hasło zastąpi zapisane.' : 'Zostaw puste, jeśli hasło jest w menedżerze haseł.'}
            error={errors.secret}
            labelAction={
              <button
                type="button"
                onClick={() => setSecret(generatePassword())}
                className="inline-flex items-center gap-1 rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                <Wand2 size={13} strokeWidth={1.75} aria-hidden />
                Wygeneruj
              </button>
            }
          >
            <PasswordInput
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
            />
          </Field>
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-caption font-medium text-fg-secondary">Hasło</span>
            <div className="flex flex-wrap items-center gap-2">
              {secretMode === 'keep' ? (
                <span
                  role="img"
                  className="inline-flex h-8 items-center gap-2 rounded-md bg-surface px-2.5 font-mono text-[13px] tracking-[0.12em] text-fg-secondary"
                  aria-label="Hasło zapisane"
                >
                  <KeyRound size={14} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
                  {CREDENTIAL_MASK}
                </span>
              ) : (
                <span className="text-body-s text-fg-secondary">Hasło zostanie usunięte z sejfu.</span>
              )}
              <span className="flex-1" />
              {secretMode === 'keep' ? (
                <>
                  <Button variant="secondary" size="s" onClick={() => setSecretMode('change')}>
                    Zmień hasło
                  </Button>
                  <Button variant="ghost" size="s" onClick={() => setSecretMode('remove')}>
                    Usuń z sejfu
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="s" onClick={() => setSecretMode('keep')}>
                  Cofnij
                </Button>
              )}
            </div>
          </div>
        )}
        {credential?.hasSecret && secretMode === 'change' && (
          <Button variant="ghost" size="s" className="-mt-2 self-start" onClick={() => setSecretMode('keep')}>
            Zostaw obecne hasło
          </Button>
        )}

        <Field
          label="Link do menedżera haseł"
          hint="Zamiast hasła albo obok niego, np. wpis w Bitwarden lub 1Password."
          error={errors.externalRef}
        >
          <Input value={externalRef} onChange={(e) => setExternalRef(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Notatka" error={errors.note}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
